// Better Auth React client. Cookie-based; talks to same-origin /api/auth/*.
//
// HoopRoster uses a minimal slice of Better Auth's public surface:
//   - useSession() — reactive React hook
//   - signIn.social({ provider, callbackURL })
//   - signOut()
//
// We re-export those bindings with explicit local interface annotations.
// Why: Better Auth's full inferred types reference internal module paths
// inside `node_modules/better-auth/dist/...`. Under our `composite: true`
// project setup, TypeScript needs every exported type to be expressible
// without referencing those internal paths (TS2742). The annotations below
// break the chain by collapsing to the slice we actually consume.

import { createAuthClient } from 'better-auth/react'

const authClient = createAuthClient({
  basePath: '/api/auth',
})

export type AppRole = 'player' | 'agent' | 'coach' | 'admin'

export interface AppProfile {
  id: string
  email: string
  display_name: string | null
  avatar_url: string | null
  role: AppRole | null
}

export interface SessionUser {
  id: string
  email: string
  name?: string | null
  image?: string | null
}

export interface SessionData {
  user: SessionUser
  session: { id: string; token: string }
}

export interface UseSessionResult {
  data: SessionData | null
  isPending: boolean
  error: { message?: string } | null
}

interface SignInApi {
  social: (opts: {
    provider: 'google'
    callbackURL?: string
  }) => Promise<unknown>
}

export const useSession: () => UseSessionResult = authClient.useSession
export const signIn: SignInApi = authClient.signIn
export const signOut: () => Promise<unknown> = authClient.signOut

// Single construction point for the OAuth callback URL. Two entry points feed
// it: the landing-page role fork (passes `intendedRole`, which AuthCallback
// forwards to /onboarding for pre-selection) and the plain sign-in controls on
// the landing page + TopNav (no argument). Omitting the hint is the correct
// returning-user path — role is already stored server-side and single-shot per
// spec §7, so AuthCallback routes on the stored value and drops any hint.
export async function startGoogleSignIn(
  intendedRole?: 'agent' | 'coach',
): Promise<unknown> {
  const callbackURL = intendedRole
    ? `/auth/callback?intended_role=${encodeURIComponent(intendedRole)}`
    : '/auth/callback'
  return signIn.social({ provider: 'google', callbackURL })
}
